/**
 * Display names for the people a record's timeline names (13-25, D-13-25-4): one User Information List read per
 * page view, the UPN when a name isn't found. Read only.
 *
 * - **One read** for every UPN not already known: `fields/EMail eq '<a>' or fields/EMail eq '<b>' …` with
 *   `EMail` and `Title` selected (the single-`eq` shape is 13-07's, proven live; at most `MAX_PER_READ` per read).
 *   The list is addressed by its GUID, falling back to its display name on a 404 (D-13-07-4).
 * - **Never throws.** A failed read leaves those UPNs unknown, and the timeline shows the UPN (who acted is still
 *   on the row). A failure is not cached, so the next page view tries again.
 * - **Held in memory only**, for the page load. Names never go to browser storage.
 */

import { errorStatus, getListId, listItemEnvelopes, odataLiteral } from './graphClient';
import type { GraphListItemEnvelope } from './graphClient';
import { USER_INFO_LIST } from './actionService';
import type { ActionIO } from './actionService';

/** The most UPNs one read asks for (keeps the filter short). */
export const MAX_PER_READ = 20;

/** UPN → display name, `''` = looked up and not found. */
const known = new Map<string, string>();

/** Test hook. */
export function resetDisplayNames(): void {
  known.clear();
}

const lower = (u: string) => u.trim().toLowerCase();

/** The names already known for these UPNs (no read). */
export function knownNames(upns: readonly string[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const u of upns) {
    const name = known.get(lower(u));
    if (name) out.set(lower(u), name);
  }
  return out;
}

/** Read the names of the UPNs not yet known, then return every known name for `upns`. */
export async function readDisplayNames(io: ActionIO, upns: readonly string[]): Promise<Map<string, string>> {
  const wanted = [...new Set(upns.map(lower).filter((u) => u.includes('@') && !known.has(u)))].slice(0, MAX_PER_READ);
  if (wanted.length > 0) {
    const query = (listId: string) =>
      listItemEnvelopes(io.client, io.siteId, listId, {
        select: ['EMail', 'Title'],
        filter: wanted.map((u) => `fields/EMail eq ${odataLiteral(u)}`).join(' or '),
        retry: io.retry,
      });
    try {
      let items: GraphListItemEnvelope[];
      try {
        items = await query(USER_INFO_LIST.id);
      } catch (err) {
        if (errorStatus(err) !== 404) throw err;
        items = await query(await getListId(io.client, io.siteId, USER_INFO_LIST.displayName));
      }
      const found = new Map<string, string>();
      for (const item of items) {
        const email = lower(String(item.fields?.EMail ?? ''));
        const title = String(item.fields?.Title ?? '').trim();
        if (email && title && wanted.includes(email)) found.set(email, title);
      }
      for (const u of wanted) known.set(u, found.get(u) ?? '');
    } catch {
      // Unknown, not cached: the UPN is shown, and the next page view asks again.
    }
  }
  return knownNames(upns);
}
